// ── Contract review checklist (layout 3, 2026-09-24) ─────────────────────────
// Every department answers each item it is responsible for — ✓ OK, ⚠ Issue
// (logged as a contract issue), or N/A (not in this contract). A department
// can set Approved / Approved with Comments only once every item is answered,
// and not plain Approved while any item is ⚠ Issue. Answers live in
// TurnoverContractReviewItems (db/04, sp_SaveContractReviewItem); the item
// lists come from REVIEW_ITEM_DEFS plus Turnover Configuration edits. Older
// layouts keep the free-text Responsibility column. Pure.
import { REVIEW_ITEM_DEFS, reviewItemsListKey } from "./contractReviewItemDefs";
import { effectiveOptions } from "./turnoverOptions";
import { layoutOf } from "./turnoverLayout";

export const REVIEW_ANSWERS = [
  { key: "ok", label: "✓ OK", title: "Reviewed — no concern" },
  { key: "issue", label: "⚠ Issue", title: "Something must change or be watched — logged as a contract issue" },
  { key: "na", label: "N/A", title: "Not in this contract" },
];

export const hasReviewChecklist = (turnover) => layoutOf(turnover) >= 3;

// The items a review row answers: active list items, plus any retired item it
// already answered (so the record still reads as it did).
export function reviewItemsFor(review, optionRows = [], answers = []) {
  const mine = (answers || []).filter(a => a.ReviewID === review?.ReviewID);
  const byKey = new Map(mine.map(a => [a.ItemKey, a]));
  const listKey = reviewItemsListKey(review?.Department);
  const all = effectiveOptions(listKey, optionRows, { includeRetired: true });
  const items = all.filter(o => o.active || byKey.has(o.key)).map(o => ({ key: o.key, label: o.label, answer: byKey.get(o.key)?.Answer || null, answeredBy: byKey.get(o.key)?.AnsweredBy || null }));
  // An answer whose key no list knows any more (e.g. a custom department's item removed) still shows.
  for (const a of mine) if (!items.some(i => i.key === a.ItemKey)) items.push({ key: a.ItemKey, label: a.Label, answer: a.Answer, answeredBy: a.AnsweredBy });
  return items;
}

export function reviewChecklistState(items = []) {
  const answered = items.filter(i => i.answer).length;
  const issues = items.filter(i => i.answer === "issue").length;
  return { total: items.length, answered, issues, complete: items.length > 0 && answered === items.length, hasChecklist: items.length > 0 };
}

// Why a status can't be chosen yet (null = allowed).
export function statusBlockReason(status, state) {
  if (!state?.hasChecklist) return null;
  if ((status === "Approved" || status === "Approved with Comments") && !state.complete)
    return `Answer every item first (${state.answered} of ${state.total})`;
  if (status === "Approved" && state.issues > 0)
    return "An item is marked ⚠ Issue — use Approved with Comments or Rejected";
  return null;
}

export const reviewItemPayload = (reviewId, item, answer, user) =>
  ({ review_id: reviewId, item_key: item.key, label: item.label, answer: answer || "", by: user?.name || "", by_email: user?.email || "" });

export { REVIEW_ITEM_DEFS, reviewItemsListKey };
